/* eslint-disable complexity */
/* eslint-disable no-unused-expressions */
/* eslint-disable max-len */
/**
 * @file 步骤组件底部操作
 * @author zhuwenyi
 */


import React, {useCallback, useEffect, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {useNavigate} from 'react-router-dom';
import {Button, Space, App, Tooltip} from 'antd';
import {flowResult} from 'mobx';
import cx from 'classnames';
import StarSvgIcon from '@assets/icons/star.svg';
import {showInsufficientCreditsModal, showStartTaskConfirmModal} from '@/modules/BillingModules';
import {MigrationRestrictTip} from '@/modules/MigrationModules';
import {useRequestErrorHandler} from '@/utils/errorHandler';
import styles from './index.module.less';


const InitialFooter: React.FC<IInitialPageStore> = observer(props => {
    const {projectsListStore, taskRunningStore, billingStore, rootStore} = props.store;
    const {jobsListStore} = projectsListStore;
    const {userStore} = rootStore;
    const {stepStore, fetchSaveUpdateJob, algorithmStore, evaluatorStore, isSpecialPlatform} = jobsListStore.selectedJobDetail;

    const {message, modal} = App.useApp();
    const {handleError} = useRequestErrorHandler();
    const navigate = useNavigate();
    const [showRechargeSuccessTip, setShowRechargeSuccessTip] = useState(false);

    /** 下一步 */
    const handleNext = useCallback(
        async () => {
            // 在查看模式下，切换到下一个步骤的查看模式
            if (stepStore.isViewMode) {
                const nextStep = Number(stepStore.viewingStep) + 1;
                if (nextStep < stepStore.totalSteps) {
                    stepStore.setViewingStep(nextStep);
                } else {
                    // 如果已经是最后一步，退出查看模式
                    stepStore.exitViewMode();
                }
                return;
            }

            // 正常模式下的逻辑
            if (!stepStore.currentStepInfo?.completed) {
                return;
            }

            // 在步骤 0（算法上传）切换前，立即保存未保存的代码
            if (stepStore.currentStep === 0 && algorithmStore.saveCodeImmediately) {
                try {
                    await algorithmStore.saveCodeImmediately();
                } catch (error) {
                    console.error('Failed to save algorithm code before next step:', error);
                    // 即使保存失败也继续，因为已经尽力了
                }
                // 清除选中的文件
                algorithmStore.setSelectedFileId(null);
            }

            // 在步骤 1（评估器上传）切换前，立即保存未保存的代码
            if (stepStore.currentStep === 1 && evaluatorStore.saveCodeImmediately) {
                try {
                    await evaluatorStore.saveCodeImmediately();
                } catch (error) {
                    console.error('Failed to save evaluator code before next step:', error);
                    // 即使保存失败也继续，因为已经尽力了
                }
            }

            stepStore.nextStep();
            fetchSaveUpdateJob(isSpecialPlatform ? {step: String(stepStore.currentStep)} : undefined);
        },
        [stepStore, algorithmStore, evaluatorStore, fetchSaveUpdateJob]
    );

    /** 上一步 */
    const handlePrev = useCallback(
        () => {
            // 在查看模式下，切换到上一个步骤的查看模式
            if (stepStore.isViewMode) {
                const prevStep = Number(stepStore.viewingStep) - 1;
                if (prevStep >= 0) {
                    stepStore.setViewingStep(prevStep);
                }
            } else {
                // 正常模式下，切换到上一个实际步骤
                stepStore.prevStep();
                fetchSaveUpdateJob(isSpecialPlatform ? {step: String(stepStore.currentStep)} : undefined);
            }
        },
        [stepStore, fetchSaveUpdateJob]
    );

    /** 退出查看模式，回到第3步 */
    const handleExitViewMode = useCallback(
        () => {
            stepStore.exitViewMode();
        },
        [stepStore]
    );

    const startTask = useCallback(
        async () => {
            try {
                // 先调用 fetchSaveUpdateJob 接口保存任务配置
                if (!isSpecialPlatform) {
                    await flowResult(jobsListStore.selectedJobDetail.fetchSaveUpdateJob());
                }

                const result = await flowResult(billingStore.fetchWithholdingFee({
                    job_id: jobsListStore.selectedJobDetail.job_id,
                    action: 'StartExp'
                }));
                const {estimatedCost, availableCredits, gap, userLevel} = billingStore.withholdingFee;

                if (result?.shouldShowInsufficientCredits) {
                    showInsufficientCreditsModal({
                        modal,
                        estimatedCost,
                        availableCredits,
                        gap,
                        userLevel,
                        onRecharge: () => {
                            navigate('/billing');
                            setShowRechargeSuccessTip(true);
                        }
                    });
                    return;
                }

                if (result) {
                    message.error(result.message || '资源点校验失败，请稍后重试');
                    return;
                }

                showStartTaskConfirmModal({
                    modal,
                    taskName: jobsListStore.selectedJobDetail.name,
                    estimatedCost,
                    availableCredits,
                    showRechargeSuccessTip,
                    onConfirm: async () => {
                        try {
                            const startResult = await flowResult(jobsListStore.selectedJobDetail.fetchStartTask());
                            if (startResult?.insufficientQuota) {
                                showInsufficientCreditsModal({
                                    modal,
                                    estimatedCost: startResult.estimatedCost,
                                    availableCredits: startResult.availableCredits,
                                    gap: startResult.gap,
                                    userLevel: startResult.userLevel,
                                    onRecharge: () => {
                                        navigate('/billing');
                                    }
                                });
                                return;
                            }
                            // 任务启动成功后，获取最新进度并启动轮询
                            await flowResult(taskRunningStore.fetchProgress(jobsListStore.selectedJobDetail.job_id));
                            taskRunningStore.startPolling();
                            message.success('任务开启成功！');
                            setShowRechargeSuccessTip(false);
                        } catch (error) {
                            handleError(error);
                        }
                    }
                });
            }
            catch (error) {
                handleError(error);
            }
        },
        [
            billingStore,
            handleError,
            isSpecialPlatform,
            jobsListStore.selectedJobDetail,
            message,
            modal,
            navigate,
            showRechargeSuccessTip,
            taskRunningStore
        ]
    );

    // 在查看模式下使用 viewingStep，否则使用 currentStep
    const displayStep = stepStore.isViewMode ? stepStore.viewingStep : stepStore.currentStep;
    const isFirstStep = displayStep === 0;
    const isLastStep = displayStep === (stepStore.totalSteps - 1);

    const hasAlgorithm = Boolean(algorithmStore.star_initial_algorithm);
    const hasInitialFiles = (algorithmStore.initial_algorithm?.length ?? 0) > 0;
    /** 是否需要显示 Tooltip */
    const showTooltip =
        isFirstStep && !hasAlgorithm && hasInitialFiles;

    useEffect(
        () => {
            if (stepStore.currentStep === 1) {
                jobsListStore.selectedJobDetail.taskDisabledState;
            }
        },
        [
            jobsListStore.selectedJobDetail.task,
            stepStore.currentStep,
            jobsListStore.selectedJobDetail.taskDisabledState
        ]
    );

    const getBtns = () => {
        const isViewMode = stepStore.isViewMode;

        if (isLastStep) {
            // 在查看模式下，显示"继续"按钮，点击后退出查看模式回到第3步
            if (isViewMode) {
                return (
                    <Button
                        type="primary"
                        onClick={handleExitViewMode}
                    >
                        继续
                    </Button>
                );
            }
            return (
                <MigrationRestrictTip enabled={userStore.isMigrationRestricted} placement="top">
                    <Button
                        type="primary"
                        className={cx(styles.linearGradientButton)}
                        loading={jobsListStore.selectedJobDetail.startLoading}
                        icon={<StarSvgIcon />}
                        iconPosition="end"
                        onClick={startTask}
                        disabled={!stepStore.currentStepInfo?.completed || userStore.isMigrationRestricted}
                    >
                        开始优化
                    </Button>
                </MigrationRestrictTip>
            );
        }

        if (showTooltip) {
            return (
                <Tooltip
                    title="请在原始工程列表选择待优化算法文件"
                    classNames={{root: styles.customTooltip}}
                >
                    <Button type="primary" disabled>继续</Button>
                </Tooltip>
            );
        }

        return (
            <Button
                type="primary"
                onClick={handleNext}
                disabled={!stepStore.isViewMode && !stepStore.currentStepInfo?.completed}
            >
                继续
            </Button>
        );
    };

    return (
        <div className={styles.container}>
            <Space size={12}>
                {!isFirstStep && (
                    <Button
                        style={{border: '1px solid var(---line-color-1, rgba(50, 54, 60, 1))'}}
                        onClick={handlePrev}
                    >
                        后退
                    </Button>
                )}
                <Space className={styles.btns}>
                    {getBtns()}
                </Space>
            </Space>
        </div>
    );
});

export default InitialFooter;
